<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import { residents, invoiceHistory, paymentHistory } from "@/stores/resident";

const route = useRoute();
const activeTab = ref("invoices");

const mockResidents = residents.map((resident) => {
  const invoices = (invoiceHistory[resident.id] || []).map((invoice) => ({
    invoiceNo: invoice.id,
    billingMonth: invoice.billingMonth,
    dueDate: invoice.dueDate,
    amount: invoice.amount,
    status: invoice.status,
  }));

  const payments = (paymentHistory[resident.id] || []).map((payment) => ({
    receiptNo: payment.receiptNo,
    paymentDate: payment.date,
    amount: payment.amount,
    method: payment.method,
    reference: payment.id,
  }));

  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const monthlyFee =
    invoices.length > 0 ? invoices[invoices.length - 1].amount : 0;

  return {
    id: resident.residentCode,
    residentId: resident.id,
    name: resident.name,
    residentCode: resident.residentCode,
    unitCode: resident.unitCode,
    residentType: resident.residentType,
    paymentStatus: resident.paymentStatus,
    phone: resident.phone,
    email: resident.email,
    lineId: "-",
    emergencyContact: resident.emergencyContact,
    emergencyPhone: resident.emergencyPhone,
    moveInDate: resident.moveInDate,
    contractEndDate: "-",
    address: resident.address,
    note: resident.note,
    totalPaid,
    outstandingBalance: resident.outstanding,
    monthlyFee,
    lastPaymentDate: resident.lastPaymentDate,
    houseNo: resident.houseNo,
    building: resident.building,
    zone: resident.zone,
    occupancyStatus: resident.occupancyStatus,
    invoices,
    payments,
  };
});

const residentId = computed(() => String(route.params.id || "").toUpperCase());

const resident = computed(
  () =>
    mockResidents.find((item) => item.id.toUpperCase() === residentId.value) ||
    null,
);

const invoiceItems = computed(() => resident.value?.invoices || []);
const paymentItems = computed(() => resident.value?.payments || []);

const residentTypeText = (type) =>
  ({ owner: "เจ้าของ", tenant: "ผู้เช่า" }[type] || "-");

const paymentStatusText = (status) =>
  ({
    paid: "ชำระครบ",
    overdue: "ค้างชำระ",
    partial: "ชำระบางส่วน",
  }[status] || "-");

const paymentStatusClass = (status) =>
  ({
    paid: "bg-success-subtle text-success",
    overdue: "bg-danger-subtle text-danger",
    partial: "bg-warning-subtle text-warning-emphasis",
  }[status] || "bg-secondary-subtle text-secondary");

const invoiceStatusClass = (status) =>
  ({
    paid: "bg-success-subtle text-success",
    overdue: "bg-danger-subtle text-danger",
    partial: "bg-warning-subtle text-warning-emphasis",
  }[status] || "bg-secondary-subtle text-secondary");

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (value) => {
  if (!value || value === "-") return "-";
  return new Date(value).toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const showPaymentModal = ref(false);

const paymentAmount = computed(() => {
  const outstanding = resident.value?.outstandingBalance || 0;
  const monthlyFee = resident.value?.monthlyFee || 0;
  return outstanding > 0 ? outstanding : monthlyFee;
});

// ถ้ามีรูปค่อยใส่รูปที่ public/images/payment-qr-demo.png
const paymentQrImage = computed(() => "/images/payment-qr-demo.png");

const openPaymentModal = () => {
  showPaymentModal.value = true;
};

const closePaymentModal = () => {
  showPaymentModal.value = false;
};

const savePaymentModal = () => {
  showPaymentModal.value = false;
  alert("บันทึกเรียบร้อย!");
};
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0" v-if="resident">
      <!-- Header -->
      <div class="mb-4">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div>
            <h4 class="fw-bold mb-1">ยูนิต / บ้าน / ห้อง ของฉัน</h4>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-outline-success rounded-3 border-success-subtle shadow-sm"
              @click="openPaymentModal"
            >
              <i class="bi bi-qr-code-scan me-1"></i>ชำระผ่าน QR Code
            </button>
          </div>
        </div>
      </div>

      <!-- Dashboard Summary -->
      <div class="row g-4 mb-4">
        <div class="col-md-6 col-xl-4">
          <div class="card border-0 shadow-sm rounded-4 h-100 stat-card">
            <div class="card-body p-4">
              <div class="d-flex align-items-start justify-content-between">
                <div class="my-auto">
                  <div class="text-muted small mb-2">ลูกบ้านที่กำลังแสดง</div>
                </div>
                <div class="stat-icon bg-primary-subtle text-primary">
                  <i class="bi bi-person-circle"></i>
                </div>
              </div>
              <div class="fw-bold fs-4 mb-1">{{ resident.name }}</div>
              <div class="text-muted small">
                {{ resident.residentCode }} •
                {{ resident.unitCode }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-xl-2">
          <div class="card border-0 shadow-sm rounded-4 h-100 stat-card">
            <div class="card-body p-4">
              <div class="d-flex align-items-start justify-content-between">
                <div class="my-auto">
                  <div class="text-muted small mb-2 me-1">ประเภท</div>
                </div>
                <div class="stat-icon bg-success-subtle text-success">
                  <i class="bi bi-house-door"></i>
                </div>
              </div>
              <div class="fw-bold fs-4 mb-1">
                {{ residentTypeText(resident.residentType) }}
              </div>
              <div class="text-muted small">สถานะการพักอาศัยในระบบ</div>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-xl-3">
          <div class="card border-0 shadow-sm rounded-4 h-100 stat-card">
            <div class="card-body p-4">
              <div class="d-flex align-items-start justify-content-between">
                <div class="my-auto">
                  <div class="text-muted small mb-2">ยอดชำระสะสม</div>
                </div>
                <div class="stat-icon bg-info-subtle text-info">
                  <i class="bi bi-cash-coin"></i>
                </div>
              </div>
              <div class="fw-bold fs-4 mb-1">
                {{ formatCurrency(resident.totalPaid) }}
              </div>
              <div class="text-muted small">ยอดชำระรวมทั้งหมด</div>
            </div>
          </div>
        </div>

        <div class="col-md-6 col-xl-3">
          <div class="card border-0 shadow-sm rounded-4 h-100 stat-card">
            <div class="card-body p-4">
              <div class="d-flex align-items-start justify-content-between">
                <div class="my-auto">
                  <div class="text-muted small mb-2">ยอดค้างปัจจุบัน</div>
                </div>
                <div class="stat-icon bg-warning-subtle text-warning-emphasis">
                  <i class="bi bi-wallet2"></i>
                </div>
              </div>
              <div class="fw-bold fs-4 mb-1">
                {{ formatCurrency(resident.outstandingBalance) }}
              </div>
              <div class="text-muted small">ยอดค้างชำระล่าสุด</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Highlight Status -->
      <div class="row g-4 mb-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-info bg-opacity-10">
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
              >
                <div>
                  <div class="fw-bold fs-5 mb-1">สถานะลูกบ้านที่แสดงอยู่</div>
                  <div class="text-muted">
                    สรุปสถานะการชำระเงินและข้อมูลล่าสุดของลูกบ้านตัวอย่าง
                  </div>
                </div>

                <div class="text-lg-end">
                  <div class="mb-2">
                    <span
                      class="badge rounded-pill px-3 py-2 fs-6"
                      :class="paymentStatusClass(resident.paymentStatus)"
                    >
                      {{ paymentStatusText(resident.paymentStatus) }}
                    </span>
                  </div>
                  <div class="text-muted small">
                    ชำระล่าสุด {{ formatDate(resident.lastPaymentDate) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail Cards -->
      <div class="row g-4 mb-4">
        <div class="col-xl-4">
          <div class="card border-0 shadow-sm rounded-4 h-100">
            <div class="card-body p-4">
              <div class="section-title mb-3">
                <i class="bi bi-telephone me-2 text-primary"></i>ข้อมูลติดต่อ
              </div>

              <div class="vstack gap-3">
                <div>
                  <div class="text-muted small">เบอร์โทรศัพท์</div>
                  <div class="fw-semibold">{{ resident.phone }}</div>
                </div>
                <div>
                  <div class="text-muted small">อีเมล</div>
                  <div class="fw-semibold">{{ resident.email }}</div>
                </div>
                <div>
                  <div class="text-muted small">LINE ID</div>
                  <div class="fw-semibold">{{ resident.lineId }}</div>
                </div>
                <div>
                  <div class="text-muted small">ผู้ติดต่อฉุกเฉิน</div>
                  <div class="fw-semibold">
                    {{ resident.emergencyContact }} ({{
                      resident.emergencyPhone
                    }})
                  </div>
                </div>
                <div>
                  <div class="text-muted small">ที่อยู่</div>
                  <div class="fw-semibold">{{ resident.address }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4">
          <div class="card border-0 shadow-sm rounded-4 h-100">
            <div class="card-body p-4">
              <div class="section-title mb-3">
                <i class="bi bi-cash-stack me-2 text-success"></i>สรุปการเงิน
              </div>

              <div class="vstack gap-3">
                <div class="summary-box bg-light rounded-4 p-3">
                  <div class="text-muted small">ค่าส่วนกลางรายเดือน</div>
                  <div class="fw-bold fs-5">
                    {{ formatCurrency(resident.monthlyFee) }}
                  </div>
                </div>

                <div class="summary-box bg-light rounded-4 p-3">
                  <div class="text-muted small">ยอดชำระสะสม</div>
                  <div class="fw-bold fs-5 text-success">
                    {{ formatCurrency(resident.totalPaid) }}
                  </div>
                </div>

                <div class="summary-box bg-light rounded-4 p-3">
                  <div class="text-muted small">ยอดค้างชำระ</div>
                  <div
                    class="fw-bold fs-5"
                    :class="
                      resident.outstandingBalance > 0
                        ? 'text-danger'
                        : 'text-success'
                    "
                  >
                    {{ formatCurrency(resident.outstandingBalance) }}
                  </div>
                </div>

                <div class="summary-box bg-light rounded-4 p-3">
                  <div class="text-muted small">สถานะการชำระ</div>
                  <div>
                    <span
                      class="badge rounded-pill px-3 py-2"
                      :class="paymentStatusClass(resident.paymentStatus)"
                    >
                      {{ paymentStatusText(resident.paymentStatus) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-4">
          <div class="card border-0 shadow-sm rounded-4 h-100">
            <div class="card-body p-4">
              <div class="section-title mb-3">
                <i class="bi bi-person-vcard me-2 text-info"></i>ข้อมูลโปรไฟล์
              </div>

              <div class="vstack gap-3">
                <div>
                  <div class="text-muted small">ชื่อ-นามสกุล</div>
                  <div class="fw-semibold">{{ resident.name }}</div>
                </div>
                <div>
                  <div class="text-muted small">รหัสลูกบ้าน</div>
                  <div class="fw-semibold">{{ resident.residentCode }}</div>
                </div>
                <div>
                  <div class="text-muted small">ยูนิต</div>
                  <div class="fw-semibold">{{ resident.unitCode }}</div>
                </div>
                <div>
                  <div class="text-muted small">ประเภท</div>
                  <div class="fw-semibold">
                    {{ residentTypeText(resident.residentType) }}
                  </div>
                </div>
                <div>
                  <div class="text-muted small">วันที่เข้าอยู่</div>
                  <div class="fw-semibold">
                    {{ formatDate(resident.moveInDate) }}
                  </div>
                </div>
                <div>
                  <div class="text-muted small">วันสิ้นสุดสัญญา</div>
                  <div class="fw-semibold">
                    {{ formatDate(resident.contractEndDate) }}
                  </div>
                </div>
                <div>
                  <div class="text-muted small">หมายเหตุ</div>
                  <div class="fw-semibold">{{ resident.note || "-" }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice / Payment History -->
      <div class="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div class="card-header bg-white border-0 p-3">
          <ul class="nav nav-pills gap-2">
            <li class="nav-item">
              <button
                type="button"
                class="nav-link rounded-pill px-4"
                :class="
                  activeTab === 'invoices' ? 'active' : 'text-dark bg-light'
                "
                @click="activeTab = 'invoices'"
              >
                <i class="bi bi-receipt me-2"></i>ประวัติใบแจ้งหนี้
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link rounded-pill px-4"
                :class="
                  activeTab === 'payments' ? 'active' : 'text-dark bg-light'
                "
                @click="activeTab = 'payments'"
              >
                <i class="bi bi-cash-coin me-2"></i>ประวัติการชำระเงิน
              </button>
            </li>
          </ul>
        </div>

        <div class="card-body p-4 bg-light-subtle">
          <!-- Invoice History -->
          <div v-if="activeTab === 'invoices'" class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>เลขที่ใบแจ้งหนี้</th>
                  <th>รอบบิล</th>
                  <th>วันครบกำหนด</th>
                  <th class="text-end">จำนวนเงิน</th>
                  <th class="text-center">สถานะ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in invoiceItems" :key="item.invoiceNo">
                  <td class="fw-semibold">{{ item.invoiceNo }}</td>
                  <td>{{ item.billingMonth }}</td>
                  <td>{{ formatDate(item.dueDate) }}</td>
                  <td class="text-end">{{ formatCurrency(item.amount) }}</td>
                  <td class="text-center">
                    <span
                      class="badge rounded-pill px-3 py-2"
                      :class="invoiceStatusClass(item.status)"
                    >
                      {{ paymentStatusText(item.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Payment History -->
          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>เลขที่ใบเสร็จ</th>
                  <th>วันที่ชำระ</th>
                  <th>ช่องทาง</th>
                  <th>อ้างอิง</th>
                  <th class="text-end">จำนวนเงิน</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in paymentItems" :key="item.receiptNo">
                  <td class="fw-semibold">{{ item.receiptNo }}</td>
                  <td>{{ formatDate(item.paymentDate) }}</td>
                  <td>{{ item.method }}</td>
                  <td>{{ item.reference }}</td>
                  <td class="text-end text-success fw-semibold">
                    {{ formatCurrency(item.amount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="container-fluid px-0">
      <div class="card border-0 shadow-sm rounded-4">
        <div class="card-body p-5 text-center">
          <div class="mb-3">
            <i class="bi bi-exclamation-circle text-danger fs-1"></i>
          </div>
          <h4 class="fw-bold mb-2">ไม่พบข้อมูล ! ยูนิต / บ้าน / ห้อง ของฉัน</h4>
          <p class="text-muted mb-0">
            ไม่พบข้อมูลจากรหัสที่ระบุ กรุณาตรวจสอบอีกครั้ง
          </p>
          <router-link to="/dashboard" class="btn btn-primary rounded-3 mt-3">
            <i class="bi bi-arrow-left me-1"></i>Go To Dashboard
          </router-link>
        </div>
      </div>
    </div>
    <div
      v-if="showPaymentModal"
      class="payment-modal-backdrop"
      @click.self="closePaymentModal"
    >
      <div class="payment-modal card border-0 shadow rounded-4">
        <div class="card-body p-4 p-lg-5">
          <div class="text-center">
            <div class="qr-box mb-2">paymentQrImage</div>

            <div class="fw-bold fs-5 mb-1">{{ resident.name }}</div>
            <div class="text-muted mb-2">
              {{ resident.residentCode }} • {{ resident.unitCode }}
            </div>

            <div class="payment-amount-box rounded-4 p-3 mb-2">
              <div class="text-muted small mb-1">ยอดที่ต้องชำระ</div>
              <div class="fw-bold fs-3 text-success">
                {{ formatCurrency(paymentAmount) }}
              </div>
            </div>

            <div class="small text-muted mb-2">
              กรุณาตรวจสอบชื่อผู้รับและจำนวนเงินก่อนยืนยันการชำระ
            </div>

            <div class="d-flex justify-content-center gap-2 flex-wrap">
              <button
                type="button"
                class="btn btn-danger rounded-3 px-4"
                @click="closePaymentModal"
              >
                <i class="bi bi-x-lg me-1"></i>ปิด
              </button>

              <button
                type="button"
                class="btn btn-success rounded-3 px-4"
                @click="savePaymentModal"
              >
                <i class="bi bi-check-circle me-1"></i>ฉันชำระแล้ว
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<style scoped>
.stat-card {
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
}

.summary-box {
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.payment-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.payment-modal {
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 1.5rem;
}

.modal-close-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
}

.qr-box {
  width: 240px;
  height: 240px;
  margin: 0 auto;
  padding: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.payment-amount-box {
  background: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.05);
}
</style>
